/**
 * Type-safe LocalStorage Utility
 * Provides type-safe access to localStorage with encoding/decoding
 */

/**
 * LocalStorage keys used in the application
 */
export const STORAGE_KEYS = {
	JWT_TOKEN: 'jwtToken',
	USER_DATA: 'transporters_user',
	ASIDE_STATUS: 'facit_asideStatus',
	DARK_MODE_STATUS: 'facit_darkModeStatus',
	PAUSED_ALERTS: 'paused_alerts',
} as const

/**
 * User data structure stored in localStorage
 */
export interface StoredUserData {
	id: number
	username: string
	email?: string
	position?: string
	firstname?: string
	lastname?: string
	phone?: string
	avatar?: string
	pinakida?: string | null
	coords?: any
	status?: string
	device?: any
	priorities?: any
	role: {
		id: number
		name?: string
		description?: string
		type: string
	}
	store_id?: number
	distributor_id?: number
}

/**
 * Get item from localStorage with optional JSON parsing
 */
export function getStorageItem<T = string>(key: string): T | null {
	try {
		const item = localStorage.getItem(key)
		if (!item) return null
		return item as T
	} catch (error) {
		console.error(`Error reading from localStorage (${key}):`, error)
		return null
	}
}

/**
 * Get and decode URL-encoded JSON from localStorage
 */
export function getEncodedStorageItem<T>(key: string): T | null {
	try {
		const item = localStorage.getItem(key)
		if (!item || item.length === 0) return null

		const decoded = decodeURIComponent(item)
		return JSON.parse(decoded) as T
	} catch (error) {
		console.error(`Error reading encoded item from localStorage (${key}):`, error)
		return null
	}
}

/**
 * Set item in localStorage with optional JSON stringification
 */
export function setStorageItem<T>(key: string, value: T): void {
	try {
		const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
		localStorage.setItem(key, stringValue)
	} catch (error) {
		console.error(`Error writing to localStorage (${key}):`, error)
	}
}

/**
 * Set URL-encoded JSON in localStorage
 */
export function setEncodedStorageItem<T>(key: string, value: T): void {
	try {
		const encoded = encodeURIComponent(JSON.stringify(value))
		localStorage.setItem(key, encoded)
	} catch (error) {
		console.error(`Error writing encoded item to localStorage (${key}):`, error)
	}
}

/**
 * Remove item from localStorage
 */
export function removeStorageItem(key: string): void {
	try {
		localStorage.removeItem(key)
	} catch (error) {
		console.error(`Error removing from localStorage (${key}):`, error)
	}
}

/**
 * Get JWT token from localStorage
 */
export function getJwtToken(): string | null {
	return getStorageItem(STORAGE_KEYS.JWT_TOKEN)
}

/**
 * Set JWT token in localStorage
 */
export function setJwtToken(token: string): void {
	setStorageItem(STORAGE_KEYS.JWT_TOKEN, token)
}

/**
 * Remove JWT token from localStorage
 */
export function removeJwtToken(): void {
	removeStorageItem(STORAGE_KEYS.JWT_TOKEN)
}

/**
 * Get user data from localStorage
 */
export function getUserData(): StoredUserData | null {
	return getEncodedStorageItem<StoredUserData>(STORAGE_KEYS.USER_DATA)
}

/**
 * Set user data in localStorage
 */
export function setUserData(userData: StoredUserData): void {
	setEncodedStorageItem(STORAGE_KEYS.USER_DATA, userData)
}

/**
 * Remove user data from localStorage
 */
export function removeUserData(): void {
	removeStorageItem(STORAGE_KEYS.USER_DATA)
}

/**
 * Get paused alerts from localStorage
 */
export function getPausedAlerts(): number[] {
	const alerts = getEncodedStorageItem<number[]>(STORAGE_KEYS.PAUSED_ALERTS)
	return alerts || []
}

/**
 * Add order ID to paused alerts
 */
export function addPausedAlert(orderId: number): void {
	const alerts = getPausedAlerts()
	if (!alerts.includes(orderId)) {
		alerts.push(orderId)
		setEncodedStorageItem(STORAGE_KEYS.PAUSED_ALERTS, alerts)
	}
}

/**
 * Alias for addPausedAlert for backwards compatibility
 */
export const pushPausedAlert = addPausedAlert

/**
 * Clear paused alerts
 */
export function clearPausedAlerts(): void {
	removeStorageItem(STORAGE_KEYS.PAUSED_ALERTS)
}

/**
 * Get dark mode status
 */
export function getDarkModeStatus(): boolean {
	const status = getStorageItem(STORAGE_KEYS.DARK_MODE_STATUS)
	return status === 'true'
}

/**
 * Set dark mode status
 */
export function setDarkModeStatus(enabled: boolean): void {
	setStorageItem(STORAGE_KEYS.DARK_MODE_STATUS, enabled.toString())
}

/**
 * Get aside status
 */
export function getAsideStatus(): boolean {
	const status = getStorageItem(STORAGE_KEYS.ASIDE_STATUS)
	return status === 'true'
}

/**
 * Set aside status
 */
export function setAsideStatus(enabled: boolean): void {
	setStorageItem(STORAGE_KEYS.ASIDE_STATUS, enabled.toString())
}
